import React, { useEffect, useState } from 'react';
import { api, money } from '../api.js';
import { errMessage } from '../i18n.js';
import { useT, useDateFmt } from '../lang.js';
import Icon from '../Icon.jsx';
import PageSkeleton from '../Skeleton.jsx';

/** Paket seçimi. Komisyonlu paketlerde aylık ücret yok → Stripe'a gidilmez. */
export default function Billing() {
  const t = useT();
  const fmt = useDateFmt();
  const [data, setData] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const pct = (bps) => `${(bps / 100).toFixed(2)}%`;
  const load = () => api('/api/billing/state').then(setData).catch((e) => setErr(errMessage(t, e)));
  useEffect(() => {
    load();
    api('/api/billing/invoices').then(setInvoices).catch(() => {});
    // eslint-disable-next-line
  }, []);

  async function choose(planKey) {
    setErr(''); setMsg(''); setBusy(true);
    try {
      const out = await api('/api/billing/checkout', { method: 'POST', body: { planKey } });
      if (out.url) location.href = out.url;
      else {
        setMsg(t('bill.activated', { plan: planKey, rate: pct(out.commissionBps) }));
        load();
      }
    } catch (e) {
      setErr(errMessage(t, e));
    } finally {
      setBusy(false);
    }
  }

  if (err && !data) return <p className="err">{err}</p>;
  if (!data) return <PageSkeleton shape="form" />;

  return (
    <>
      <div className="card">
        <h2>{t('bill.yourPlan')}</h2>
        <div className="row">
          <b>{data.current.planKey || '—'}</b>
          <span className={`badge ${data.current.subscriptionStatus === 'active' ? 'ok' : 'warn'}`}>
            {data.current.subscriptionStatus || t('none')}
          </span>
          {data.current.commissionBps > 0 && (
            <span className="badge warn">{t('bill.commissionBadge', { rate: pct(data.current.commissionBps) })}</span>
          )}
          {data.current.commissionNegotiated && <span className="badge ok">{t('ov.negotiatedRate')}</span>}
        </div>
        {data.current.trialEndsAt && new Date(data.current.trialEndsAt) > new Date() && (
          <p className="muted" style={{ marginBottom: 0 }}>
            {t('bill.trialEndsIn', { n: Math.ceil((new Date(data.current.trialEndsAt) - Date.now()) / 864e5) })}
          </p>
        )}
      </div>

      <div className="grid2">
        {data.plans.map((p) => {
          const current = p.key === data.current.planKey;
          return (
            <div className="card" key={p.key} style={{ borderColor: current ? 'var(--accent)' : undefined }}>
              <h2>{p.name}</h2>
              <p style={{ fontSize: 20, margin: '0 0 4px' }}>
                {p.monthlyMinor
                  ? t('bill.perMonth', { amount: money(p.monthlyMinor, data.currency) })
                  : t('bill.noMonthlyFee')}
              </p>
              <p className="muted" style={{ marginTop: 0 }}>
                {p.commissionBps > 0
                  ? <>{t('bill.commissionLine', { rate: pct(p.commissionBps) })}
                      <br />{t('bill.commissionSub')}
                      {/* Platform payı ≠ toplam maliyet: kartın kendi kabul
                          ücreti restoranın Stripe hesabından ayrıca çıkıyor ve
                          ülkeye/kart tipine göre değişiyor. Tek bir sayı
                          yazmak yanıltıcı olurdu. */}
                      <br />{t('bill.cardFeeNote')}
                      {/* Oran ülkeye göre değişiyorsa restoran bunu bilsin —
                          sonradan "neden %5 kesilmiş" sorusu gelmesin. */}
                      {p.commissionCountry && p.commissionBps !== p.commissionBaseBps && (
                        <><br /><span className="badge warn">{t('bill.countryRate', { country: p.commissionCountry })}</span>{' '}
                          {t('bill.countryRateNote')}</>
                      )}</>
                  : <>{t('bill.noCommission')}<br />{t('bill.onlineOff')}</>}
              </p>
              <ul className="muted" style={{ fontSize: 13, paddingLeft: 18, margin: '8px 0' }}>
                {Object.entries(p.features)
                  .filter(([, v]) => v.enabled)
                  .map(([k, v]) => (
                    <li key={k}>{t(`feat.${k}`)}{k === 'max_tables' ? ` (${v.limitInt ?? t('unlimited')})` : ''}</li>
                  ))}
              </ul>
              <button className={current ? '' : 'primary'} disabled={busy || current} onClick={() => choose(p.key)}>
                <Icon n={current ? 'check' : p.monthlyMinor ? 'card' : 'tag'} />
                {current ? t('bill.currentPlan') : p.monthlyMinor ? t('bill.subscribe') : t('bill.selectPlan')}
              </button>
            </div>
          );
        })}
      </div>

      {invoices.length > 0 && (
        <div className="card">
          <h2>{t('bill.invoices')}</h2>
          <table>
            <thead><tr><th>{t('bill.date')}</th><th>{t('bill.number')}</th><th>{t('amount')}</th><th>{t('status')}</th><th></th></tr></thead>
            <tbody>
              {invoices.map((i) => (
                <tr key={i.id}>
                  <td className="muted">{fmt.date(i.date)}</td>
                  <td>{i.number || '—'}</td>
                  <td>{money(i.amountMinor, i.currency)}</td>
                  <td>{i.status}</td>
                  <td>{i.pdf && <a href={i.pdf} target="_blank" rel="noreferrer">PDF</a>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {msg && <p className="ok">{msg}</p>}
      {err && <p className="err">{err}</p>}
    </>
  );
}

